/**
 * Unified logger that wraps console methods with structured context
 * and forwards errors to Datadog RUM on the client.
 *
 * Usage:
 *   import { logger } from "@/lib/logger";
 *   const log = logger("MyModule");
 *   log.info("something happened", { key: "value" });
 *   log.error("request failed", error);
 */

type LogData = Record<string, unknown>

interface Logger {
  /** Informational messages — suppressed in production on the client. */
  info: (message: string, data?: LogData) => void
  /** Warnings — always emitted. */
  warn: (message: string, data?: LogData) => void
  /** Errors — always emitted and forwarded to Datadog RUM on the client. */
  error: (message: string, error?: unknown, data?: LogData) => void
  /** Debug messages — only emitted in development. */
  debug: (message: string, data?: LogData) => void
}

export type { Logger }

const isServer = typeof window === "undefined"
const isDevelopment = process.env.NODE_ENV === "development"
const isTest = process.env.NODE_ENV === "test"

function formatTag(tag: string): string {
  return `[${tag}]`
}

function safeSerialize(value: unknown): string {
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function getErrorMessage(error: Error): string {
  return error.message.trim()
}

export function getDatadogRumError(message: string, error: unknown): Error {
  if (!(error instanceof Error)) {
    return new Error(message)
  }

  if (getErrorMessage(error)) {
    return error
  }

  const contextualError = new Error(message)
  contextualError.name = error.name || contextualError.name
  if (error.stack) {
    contextualError.stack = error.stack
  }
  return contextualError
}

function getErrorContext(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      errorMessage: error.message,
      errorStack: error.stack,
    }
  }

  if (error != null) {
    return {
      errorValue: safeSerialize(error),
    }
  }

  return {}
}

/**
 * Attempt to get `datadogRum` lazily so this module can be imported on the
 * server without pulling in browser-only code.
 */
function getDatadogRum() {
  if (isServer) {
    return undefined
  }
  try {
    // Dynamic require so the import is skipped on the server and doesn't
    // break tree-shaking for server bundles.
    const { datadogRum } = require("@datadog/browser-rum") as Record<
      string,
      unknown
    >
    return datadogRum as
      | {
          addError?: (error: unknown, context?: Record<string, unknown>) => void
        }
      | undefined
  } catch {
    return undefined
  }
}

/**
 * Attempt to get `datadogLogs` lazily so this module can be imported on the
 * server without pulling in browser-only code.
 */
function getDatadogLogs() {
  if (isServer) {
    return undefined
  }
  try {
    // Dynamic require so the import is skipped on the server and doesn't
    // break tree-shaking for server bundles.
    const { datadogLogs } = require("@datadog/browser-logs") as Record<
      string,
      unknown
    >
    return datadogLogs as
      | {
          logger?: {
            warn?: (message: string, context?: Record<string, unknown>) => void
            error?: (message: string, context?: Record<string, unknown>) => void
          }
        }
      | undefined
  } catch {
    return undefined
  }
}

export function logger(tag: string): Logger {
  const prefix = formatTag(tag)

  return {
    info(message, data) {
      // Suppress informational logs in production on the client to keep the
      // browser console clean.  Server-side info logs are always useful for
      // operational visibility (picked up by the Datadog agent).
      if (!isServer && !isDevelopment && !isTest) {
        return
      }
      if (data) {
        // eslint-disable-next-line no-console -- logger transport intentionally writes to console sinks
        console.log(prefix, message, data)
      } else {
        // eslint-disable-next-line no-console -- logger transport intentionally writes to console sinks
        console.log(prefix, message)
      }
    },

    warn(message, data) {
      if (data) {
        // eslint-disable-next-line no-console -- logger transport intentionally writes to console sinks
        console.warn(prefix, message, data)
      } else {
        // eslint-disable-next-line no-console -- logger transport intentionally writes to console sinks
        console.warn(prefix, message)
      }

      if (!isServer) {
        const logs = getDatadogLogs()
        logs?.logger?.warn?.(message, { tag, ...data })
      }
    },

    error(message, error, data) {
      // Suppress console.error in production client; Datadog handles it
      if (isDevelopment || isTest || isServer) {
        // eslint-disable-next-line no-console -- logger transport intentionally writes to console sinks
        console.error(
          prefix,
          message,
          ...(error ? [error] : []),
          ...(data ? [data] : []),
        )
      }

      // Forward to Datadog RUM so errors surface in the Error Tracking tab.
      if (!isServer) {
        const rum = getDatadogRum()
        rum?.addError?.(getDatadogRumError(message, error), {
          tag,
          ...getErrorContext(error),
          ...data,
        })

        const logs = getDatadogLogs()
        logs?.logger?.error?.(message, {
          tag,
          ...getErrorContext(error),
          ...data,
        })
      }
    },

    debug(message, data) {
      if (!isDevelopment) {
        return
      }
      if (data) {
        // eslint-disable-next-line no-console -- logger transport intentionally writes to console sinks
        console.debug(prefix, message, data)
      } else {
        // eslint-disable-next-line no-console -- logger transport intentionally writes to console sinks
        console.debug(prefix, message)
      }
    },
  }
}
